import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Idea, IdeaTask, TaskStatus } from '@/lib/types';
import { useIdeas } from '@/hooks/useIdeas';
import { useIdeaPlan } from '@/hooks/useIdeaPlan';
import { usePlanGeneration } from '@/hooks/usePlanGeneration';
import { PlanIntakeModal, type PlanIntakeData } from '@/components/plan/PlanIntakeModal';
import { PlanKanban } from '@/components/plan/PlanKanban';
import { PlanList } from '@/components/plan/PlanList';
import { PlanGrid } from '@/components/plan/PlanGrid';
import { PlanTimeline } from '@/components/plan/PlanTimeline';
import { CoFounderBrief } from '@/components/plan/CoFounderBrief';
import { AddTaskForm } from '@/components/plan/AddTaskForm';
import { NextActionsPanel } from '@/components/plan/NextActionsPanel';
import { TaskDetailSheet } from '@/components/plan/TaskDetailSheet';
import { GrowthIcon } from '@/components/GrowthIcon';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Calendar, 
  LayoutGrid, 
  List, 
  Plus,
  Clock,
  Target,
  RefreshCw,
  Loader2,
  Sparkles,
  Grid3X3,
  GanttChart,
  Droplets,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { calculateDaysSinceWatered } from '@/lib/time-utils';

type ViewMode = 'kanban' | 'list' | 'grid' | 'timeline';

export default function Plan() {
  const { ideaId } = useParams<{ ideaId?: string }>();
  const navigate = useNavigate();
  const { ideas, loading: ideasLoading } = useIdeas();
  
  // Determine which idea to show
  const selectedIdeaId = useMemo(() => {
    if (ideaId) return ideaId;
    if (!ideas.length) return null;
    
    // Priority: focused idea > most recently watered active idea
    const focusedIdea = ideas.find(i => i.is_focus && i.status === 'active');
    if (focusedIdea) return focusedIdea.id;
    
    const activeIdeas = ideas.filter(i => i.status === 'active');
    if (activeIdeas.length === 0) return null;
    
    // Sort by last_watered_at descending
    const sorted = [...activeIdeas].sort((a, b) => {
      const aDate = a.last_watered_at ? new Date(a.last_watered_at).getTime() : 0;
      const bDate = b.last_watered_at ? new Date(b.last_watered_at).getTime() : 0;
      return bDate - aDate;
    });
    
    return sorted[0].id;
  }, [ideaId, ideas]);

  // Auto-navigate to the selected idea if no ideaId in URL
  useEffect(() => {
    if (!ideaId && selectedIdeaId) {
      navigate(`/app/plan/${selectedIdeaId}`, { replace: true });
    }
  }, [ideaId, selectedIdeaId, navigate]);

  const selectedIdea = ideas.find(i => i.id === selectedIdeaId) || null;
  const activeIdeas = ideas.filter(i => i.status === 'active');

  const {
    planMeta,
    tasks,
    loading: planLoading,
    generating: legacyGenerating,
    createPlan,
    updatePlan,
    updateTask,
    generatePlanWithAI,
    insertGeneratedTasks,
    regeneratePlan,
    addTask,
    deleteTask,
  } = useIdeaPlan(selectedIdeaId || '');

  // New hardened plan generation hook
  const {
    generating: hardenedGenerating,
    error: planGenerationError,
    generatedPlan,
    retry: retryGeneration,
    clearError,
  } = usePlanGeneration(selectedIdeaId || '');

  // Use hardened generating state when available
  const generating = hardenedGenerating || legacyGenerating;

  const [showIntake, setShowIntake] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [timelineEdits, setTimelineEdits] = useState(0);
  const [stepExpanded, setStepExpanded] = useState(false);
  const [editingTask, setEditingTask] = useState<IdeaTask | null>(null);
  const [lastIntakeData, setLastIntakeData] = useState<PlanIntakeData | null>(null);

  const doingCount = tasks.filter(t => t.status === 'doing').length;

  // Track timeline edits for Co-Founder Brief
  const handleTimelineEdit = useCallback(async (taskId: string, updates: Partial<IdeaTask>) => {
    setTimelineEdits(prev => prev + 1);
    return updateTask(taskId, updates);
  }, [updateTask]);

  const handleCreatePlan = async (data: PlanIntakeData) => {
    if (!selectedIdea) return;

    // Save intake data for potential retry
    setLastIntakeData(data);
    clearError();

    const intake = {
      ...data,
      plan_start_date: new Date().toISOString().split('T')[0],
      plan_end_date: null,
    };

    const plan = await createPlan(intake);
    if (!plan) {
      toast.error('Failed to create plan');
      return;
    }

    toast.info('Generating your plan with AI...', { duration: 3000 });
    
    const result = await generatePlanWithAI(selectedIdea, intake);
    if (!result) {
      return;
    }

    const tasksInserted = await insertGeneratedTasks(result.tasks);
    if (!tasksInserted) {
      toast.error('Failed to save generated tasks');
      return;
    }

    await updatePlan({
      plan_end_date: result.plan_window.end_date,
      first_irreversible_step: result.first_irreversible_step.title,
      first_irreversible_rationale: result.first_irreversible_step.rationale,
    });
  };

  // Handle retry with new request ID
  const handleRetry = async () => {
    if (!selectedIdea || !lastIntakeData) return;
    
    clearError();
    toast.info('Retrying plan generation...', { duration: 2000 });
    
    const intake = {
      ...lastIntakeData,
      plan_start_date: new Date().toISOString().split('T')[0],
      plan_end_date: null,
    };
    
    const result = await retryGeneration(selectedIdea, intake);
    if (result) {
      const tasksInserted = await insertGeneratedTasks(result.tasks);
      if (tasksInserted) {
        await updatePlan({
          plan_end_date: result.plan_window.end_date,
          first_irreversible_step: result.first_irreversible_step.title,
          first_irreversible_rationale: result.first_irreversible_step.rationale,
        });
      }
    }
  };

  const handleRegeneratePlan = async () => {
    if (!planMeta || !selectedIdea) return;

    const intake = {
      hours_per_week: planMeta.hours_per_week,
      idea_type: planMeta.idea_type,
      current_state: planMeta.current_state,
      constraint_text: planMeta.constraint_text,
      done_definition: planMeta.done_definition,
      target_date: planMeta.target_date,
      plan_start_date: planMeta.plan_start_date,
      plan_end_date: planMeta.plan_end_date,
    };

    await regeneratePlan(selectedIdea, intake);
  };

  // Wrapper for task reordering (Kanban)
  const handleReorderTasks = async (taskId: string, newPosition: number, newStatus: TaskStatus) => {
    await updateTask(taskId, { position: newPosition, status: newStatus });
    return true;
  };

  const handleIdeaChange = (newIdeaId: string) => {
    navigate(`/app/plan/${newIdeaId}`);
  };

  // Loading state
  if (ideasLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="h-6 w-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Empty state - no ideas
  if (ideas.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4 max-w-sm">
          <h2 className="text-lg font-medium">No ideas yet</h2>
          <p className="text-sm text-muted-foreground">
            Plant your first seed in the Garden to start planning.
          </p>
          <Button onClick={() => navigate('/app')} className="gap-2">
            <Plus className="h-4 w-4" />
            Go to Garden
          </Button>
        </div>
      </div>
    );
  }

  // No active ideas
  if (activeIdeas.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4 max-w-sm">
          <h2 className="text-lg font-medium">No active ideas</h2>
          <p className="text-sm text-muted-foreground">
            All your ideas are withered or archived. Return to the Garden to revive one.
          </p>
          <Button onClick={() => navigate('/app')} variant="outline" className="gap-2">
            Go to Garden
          </Button>
        </div>
      </div>
    );
  }

  const daysSinceWatered = selectedIdea ? calculateDaysSinceWatered(selectedIdea) : 0;
  const aiTasks = tasks.filter(t => t.source === 'ai');
  const userTasks = tasks.filter(t => t.source === 'user');

  return (
    <div className="h-full flex flex-col min-w-0">
      {/* === Idea Context Bar === */}
      <div className="shrink-0 border-b border-border bg-background px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* Idea Selector */}
          <Select value={selectedIdeaId || ''} onValueChange={handleIdeaChange}>
            <SelectTrigger className="w-[200px] sm:w-[240px]">
              <SelectValue placeholder="Select an idea" />
            </SelectTrigger>
            <SelectContent className="bg-background">
              {activeIdeas.map((idea) => (
                <SelectItem key={idea.id} value={idea.id}>
                  <div className="flex items-center gap-2">
                    <GrowthIcon stage={idea.growth_stage} size={14} />
                    <span className="truncate">{idea.title || 'Untitled'}</span>
                    {idea.is_focus && (
                      <Badge variant="secondary" className="text-[9px] px-1">Focus</Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedIdea && (
            <>
              {/* Focus Badge */}
              {selectedIdea.is_focus && (
                <Badge variant="default" className="text-[10px] gap-1 shrink-0">
                  <Target className="h-2.5 w-2.5" />
                  Focus
                </Badge>
              )}

              {/* Last Watered */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                <Droplets className="h-3 w-3" />
                <span>{daysSinceWatered === 0 ? 'Today' : `${daysSinceWatered}d ago`}</span>
              </div>
            </>
          )}

          {/* Plan Meta (if plan exists) */}
          {planMeta && (
            <>
              <div className="hidden sm:block h-4 w-px bg-border" />
              
              <Badge variant="secondary" className="text-[10px] shrink-0">
                v{planMeta.plan_version}
              </Badge>

              <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                <Clock className="h-3 w-3" />
                <span>{planMeta.hours_per_week}h/wk</span>
              </div>

              {planMeta.plan_start_date && planMeta.plan_end_date && (
                <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                  <Calendar className="h-3 w-3" />
                  <span>
                    {format(new Date(planMeta.plan_start_date), 'MMM d')} → {format(new Date(planMeta.plan_end_date), 'MMM d')}
                  </span>
                </div>
              )}

              {planMeta.target_date && (
                <div className="hidden lg:flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                  <Target className="h-3 w-3" />
                  <span>Target: {format(new Date(planMeta.target_date), 'MMM d')}</span>
                </div>
              )}

              {/* Regenerate Button */}
              <div className="ml-auto shrink-0">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 text-xs gap-1.5 text-muted-foreground hover:text-foreground" 
                      disabled={generating}
                    >
                      <RefreshCw className="h-3 w-3" />
                      <span className="hidden sm:inline">Regenerate</span>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Regenerate Plan?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will replace all {aiTasks.length} AI-generated tasks with a new set. 
                        Your {userTasks.length} manually added tasks will be kept.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleRegeneratePlan}>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Regenerate
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </>
          )}
        </div>
      </div>

      {/* === Plan Content === */}
      {planLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="h-6 w-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : !planMeta ? (
        // No plan exists - show CTA or error state
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center space-y-4 max-w-sm">
            {/* Error Banner with Retry */}
            {planGenerationError && (
              <Alert variant="destructive" className="text-left">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="flex flex-col gap-2">
                  <span>{planGenerationError}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    disabled={generating || !lastIntakeData}
                    className="w-fit gap-1.5"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {!planGenerationError && (
              <>
                <h3 className="text-lg font-medium">No plan yet</h3>
                <p className="text-sm text-muted-foreground">
                  Create an execution plan to turn this idea into reality, one step at a time.
                </p>
              </>
            )}
            
            <Button 
              onClick={() => setShowIntake(true)} 
              className="gap-2"
              disabled={generating}
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Generate Plan
                </>
              )}
            </Button>

            <PlanIntakeModal
              open={showIntake}
              onOpenChange={setShowIntake}
              onSubmit={handleCreatePlan}
              ideaTitle={selectedIdea?.title || undefined}
            />
          </div>
        </div>
      ) : (
        // Plan exists - full-width execution workspace
        <>
          {/* Generating overlay */}
          {generating && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm font-medium">Generating your plan...</p>
                <p className="text-xs text-muted-foreground">This may take a moment</p>
              </div>
            </div>
          )}

          {/* Collapsible First Irreversible Step */}
          {planMeta.first_irreversible_step && (
            <Collapsible open={stepExpanded} onOpenChange={setStepExpanded} className="shrink-0 border-b border-border">
              <div className="px-4 py-2 bg-muted/10">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5 text-[10px] font-medium text-primary uppercase tracking-wide shrink-0">
                    <Target className="h-3 w-3" />
                    First Irreversible Step
                  </div>
                  <p className="text-sm font-medium truncate flex-1 min-w-0">
                    {planMeta.first_irreversible_step}
                  </p>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0">
                      {stepExpanded ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                  {planMeta.first_irreversible_rationale && (
                    <p className="text-xs text-muted-foreground italic mt-2 leading-relaxed">
                      {planMeta.first_irreversible_rationale}
                    </p>
                  )}
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {/* Sticky View Switcher */}
          <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-2 shrink-0">
            <div className="flex items-center justify-between gap-4">
              <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
                <TabsList className="h-9">
                  <TabsTrigger value="kanban" className="text-xs gap-1.5 px-3">
                    <LayoutGrid className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Board</span>
                  </TabsTrigger>
                  <TabsTrigger value="list" className="text-xs gap-1.5 px-3">
                    <List className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">List</span>
                  </TabsTrigger>
                  <TabsTrigger value="grid" className="text-xs gap-1.5 px-3">
                    <Grid3X3 className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Grid</span>
                  </TabsTrigger>
                  <TabsTrigger value="timeline" className="text-xs gap-1.5 px-3">
                    <GanttChart className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Timeline</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="flex items-center gap-3">
                <CoFounderBrief tasks={tasks} timelineEdits={timelineEdits} />
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {tasks.filter(t => t.status === 'done').length}/{tasks.length} done
                </span>
              </div>
            </div>
          </div>

          {/* Full-width Execution Canvas */}
          <div className="flex-1 overflow-auto min-h-0 min-w-0">
            {viewMode === 'kanban' && (
              <div className="h-full p-4">
                <PlanKanban
                  tasks={tasks}
                  onUpdateTask={updateTask}
                  onDeleteTask={deleteTask}
                  onReorderTasks={handleReorderTasks}
                  onEditTask={setEditingTask}
                />
              </div>
            )}
            
            {viewMode === 'list' && (
              <div className="p-4">
                <PlanList
                  tasks={tasks}
                  onUpdateTask={updateTask}
                  onDeleteTask={deleteTask}
                  onEditTask={setEditingTask}
                />
              </div>
            )}
            
            {viewMode === 'grid' && (
              <div className="p-4">
                <PlanGrid tasks={tasks} onEditTask={setEditingTask} />
              </div>
            )}
            
            {viewMode === 'timeline' && (
              <div className="h-full p-4">
                <PlanTimeline
                  tasks={tasks}
                  planMeta={planMeta}
                  onUpdateTask={handleTimelineEdit}
                />
              </div>
            )}
          </div>

          {/* Next Actions Panel - sticky at bottom */}
          <NextActionsPanel
            ideaId={selectedIdeaId!}
            tasks={tasks}
            risks={selectedIdea?.risks_unknowns || []}
            onTasksCreated={() => {}}
            onExperimentsCreated={() => {}}
          />

          {/* Add task footer */}
          <div className="shrink-0 border-t border-border bg-background px-4 py-3">
            <AddTaskForm onAdd={addTask} />
          </div>

          {/* Task Detail Sheet */}
          <TaskDetailSheet
            task={editingTask}
            open={!!editingTask}
            onOpenChange={(open) => !open && setEditingTask(null)}
            onUpdateTask={updateTask}
            onDeleteTask={deleteTask}
            doingCount={doingCount}
          />
        </>
      )}
    </div>
  );
}
